"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import ServerCrash from "lucide-react/dist/esm/icons/server-crash";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw";
import Home from "lucide-react/dist/esm/icons/house";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left";
import { Button } from "@/components/ui/button";
import { reportErrorToDiscord } from "@/lib/error-reporting";
import { isChunkLoadError, recoverFromChunkError } from "@/lib/chunk-recovery";
import { isDomMutationError } from "@/lib/error-suppression";
import { claimDomErrorAutoReset } from "@/lib/dom-mutation-guard";
import { DISCORD_TRACKED_INVITE_URL } from "@/components/ui/discord-cta";
import { humanizeError, getErrorCode, AppErrorCode } from "@/lib/error-humanizer";
import { logEvent } from "@/lib/events";

interface RouteErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
  /** Shown in the badge above the title. Defaults to the resolved error code. */
  label?: string;
  /** Where the "Go back" link should point. Defaults to "/". */
  backHref?: string;
  backLabel?: string;
}

/**
 * Reusable segment-level error UI for Next.js `error.tsx` files.
 * Reports the error automatically on first render.
 */
export function RouteError({
  error,
  reset,
  label,
  backHref = "/",
  backLabel = "Go Home",
}: RouteErrorProps) {
  const code = getErrorCode(error);
  const pathname = usePathname();
  // A browser extension / translator moved a node React owned, so React's
  // commit threw (`removeChild` NotFoundError). The tree is fine — re-render
  // it instead of showing "Something went wrong" and paging Discord. The
  // budget is per route per page load, so a genuinely recurring throw still
  // ends up on the normal error UI (and in the feed) after a few tries.
  const foreignDomError = isDomMutationError(error.message, error.stack, error.name);
  const autoResetDecision = useRef<boolean | null>(null);
  if (autoResetDecision.current === null) {
    autoResetDecision.current = foreignDomError && claimDomErrorAutoReset(pathname ?? "route");
  }
  const autoResetting = autoResetDecision.current;

  useEffect(() => {
    if (autoResetting) {
      reset();
      return;
    }
    // A chunk that 404s means this tab is running a build we've since replaced.
    // Reload onto the current one rather than reporting a crash the code didn't
    // cause. (Falls through to normal handling if the reload cooldown blocks it,
    // so a genuinely missing chunk still surfaces.)
    if (isChunkLoadError(error) && recoverFromChunkError()) return;

    const isAccessError =
      code === AppErrorCode.FORBIDDEN ||
      code === AppErrorCode.UNAUTHORIZED ||
      code === AppErrorCode.SUSPENDED;

    reportErrorToDiscord({
      source: "RouteError",
      userMessage: humanizeError(error),
      error,
      severity: isAccessError ? "warning" : "error",
      metadata: {
        digest: error.digest,
        error_code: code,
      },
    });

    void logEvent(
      "route_error",
      { url: pathname, error: error.message },
      { severity: "critical" },
    );
  }, [error, code, pathname, autoResetting, reset]);

  if (autoResetting) return null;

  const badgeText = label ?? (error.digest ? `ERR · ${error.digest}` : "ROUTE ERROR");
  const friendlyMessage = humanizeError(error);

  return (
    <div className="min-h-[60dvh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-destructive/15 blur-xl rounded-full" />
          <div className="relative rounded-2xl bg-destructive/10 border border-destructive/20 p-5">
            <ServerCrash className="h-10 w-10 text-destructive" />
          </div>
        </div>

        {/* Badge */}
        <div className="mb-4">
          <span className="inline-block font-mono text-xs uppercase tracking-widest text-destructive/70 bg-destructive/5 px-3 py-1 rounded-full border border-destructive/10">
            {badgeText}
          </span>
        </div>

        <h2 className="font-display text-2xl font-bold mb-2">Something went wrong</h2>
        <p className="text-muted-foreground mb-4 max-w-sm mx-auto leading-relaxed">
          {friendlyMessage}
        </p>
        <p className="text-xs text-muted-foreground/60 mb-6">
          This error has been automatically reported.
        </p>

        {/* Dev-only details */}
        {process.env.NODE_ENV === "development" && (
          <div className="mb-6 p-4 bg-destructive/5 rounded-lg border border-destructive/20 text-left overflow-auto max-h-28">
            <p className="font-mono text-xs text-destructive break-all">{error.message}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={reset} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Try Again
          </Button>
          <Button asChild variant="outline" className="gap-2 border-border/50">
            <Link href={backHref}>
              {backHref === "/" ? (
                <Home className="h-4 w-4" />
              ) : (
                <ArrowLeft className="h-4 w-4" />
              )}
              {backLabel}
            </Link>
          </Button>
        </div>

        <p className="mt-5 text-xs text-muted-foreground">
          Still stuck? Ask in our{" "}
          <a
            href={DISCORD_TRACKED_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-discord hover:underline font-medium"
          >
            Discord
          </a>{" "}
          — someone's usually around.
        </p>
      </div>
    </div>
  );
}
